import { createSlice } from "@reduxjs/toolkit";
import { Toastify } from "../../../components/Toaster";
import { addingSideEffects, deletingSideEffects, errorSideEffects, loadingSideEffects, successSideEffects, updatingSideEffects } from "..";
import { getOutcomeTrackingState, getRequestTrackingState, MutationTrackingState } from "../../../services/analytics/tracking";

export interface SMTPSettings {
  Id: string;
  UserName: string;
  SmtpHost: string;
  SmtpPort: number;
  FromEmailId: string;
  FromName: string;
  IsSsl: boolean;
  Key: string;
  Secret: string;
  IsDefaultConfig: boolean;
  Name: string;
}

export interface SMTPSettingsState {
  Data: SMTPSettings[];
  isLoading: boolean;
  isUpdating: boolean;
  isSuccess: boolean;
  isAdding: boolean;
  isDeleting: boolean;
  isError: boolean;
  error: string | null;
}

export const SMTPSettingsSlice = createSlice({
  name: "smtpSetting",
  initialState: {
    Data: [],
    isLoading: false,
    isUpdating: false,
    isAdding: false,
    isSuccess: false,
    isDeleting: false,
    isError: false,
    error: null
  },
  reducers: {
    getSMTPSettingsAction(state) {
      Object.assign(state, loadingSideEffects);
    },
    getSMTPSettingsSuccess(state, action) {
      state.Data = action.payload.Data;
      Object.assign(state, successSideEffects);
    },
    getSMTPSettingsError(state, action) {
      Object.assign(state, errorSideEffects);
      state.error = action.payload;
    },
    updateSMTPSettingsAction(state, action) {
      Object.assign(state, updatingSideEffects, getRequestTrackingState(action));
    },
    updateSMTPSettingsSuccess(state, action) {
      Object.assign(state, successSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "success"));
      const updatedIndex = state.Data.findIndex((setting) => setting.Id === action.payload.Id);
      if (updatedIndex !== -1) {
        state.Data[updatedIndex] = action.payload;
      }
      Toastify("success", "SMTP Settings updated successfully");
    },
    updateSMTPSettingsError(state, action) {
      Object.assign(state, errorSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "error"));
      state.error = action.payload;
      Toastify("error", action.payload);
    },
    addSMTPSettingsAction(state, action) {
      Object.assign(state, addingSideEffects, getRequestTrackingState(action));
    },
    addSMTPSettingsSuccess(state, action) {
      Object.assign(state, successSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "success"));
      state.Data.push(action.payload);
      Toastify("success", "SMTP setting added successfully");
    },
    addSMTPSettingsError(state, action) {
      Object.assign(state, errorSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "error"));
      state.error = action.payload;
      Toastify("error", action.payload);
    },
    deleteSMTPSettingsAction(state, action) {
      Object.assign(state, deletingSideEffects, getRequestTrackingState(action));
    },
    deleteSMTPSettingsSuccess(state, action) {
      Object.assign(state, successSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "success"));
      const deletedId = action.payload.emailConfigId;
      state.Data = state.Data?.filter((setting: SMTPSettings) => setting.Id !== deletedId);
      Toastify("success", "SMTP Setting deleted successfully");
    },
    deleteSMTPSettingsError(state, action) {
      Object.assign(state, errorSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "error"));
      state.error = action.payload;
      Toastify("error", action.payload);
    },
    setDefaultSMTPSettingsAction(state, action) {
      Object.assign(state, updatingSideEffects, getRequestTrackingState(action));
    },
    setDefaultSMTPSettingsSuccess(state, action) {
      Object.assign(state, successSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "success"));
      const updatedIndex = state.Data.findIndex((setting) => setting.Id === action.payload);
      if (updatedIndex !== -1) {
        state.Data.forEach((setting) => {
          setting.IsDefaultConfig = setting.Id === action.payload;
        });
      }
      Toastify("success", "Default SMTP provider updated successfully");
    },
    setDefaultSMTPSettingsError(state, action) {
      Object.assign(state, errorSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "error"));
      state.error = action.payload;
      Toastify("error", action.payload);
    }
  }
});

export const {
  getSMTPSettingsAction,
  getSMTPSettingsSuccess,
  getSMTPSettingsError,
  updateSMTPSettingsAction,
  updateSMTPSettingsSuccess,
  updateSMTPSettingsError,
  addSMTPSettingsAction,
  addSMTPSettingsSuccess,
  addSMTPSettingsError,
  deleteSMTPSettingsAction,
  deleteSMTPSettingsSuccess,
  deleteSMTPSettingsError,
  setDefaultSMTPSettingsAction,
  setDefaultSMTPSettingsSuccess,
  setDefaultSMTPSettingsError
} = SMTPSettingsSlice.actions;

export default SMTPSettingsSlice.reducer;
